import * as _ from 'lodash';
import { useMemo } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
  finleyAssertUnreachable,
} from '@finley-technologies/common';

import {
  FacilitiesTableAttributeValueFragmentGQLType,
  SuccessFinancialCovenantGQLType,
  SuccessPortfolioCovenantGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  FacilitiesTableRowData,
  FacilitiesTableTotalRowData,
} from '../facilitiesTableOptions';
import { useGetBalanceSheetExposureDataQuery } from '../getBalanceSheetExposureData.gql.generated';
import {
  GetFacilitiesTableDataQueryGQLType,
  useGetFacilitiesTableDataQuery,
} from '../getFacilitiesTableData.gql.generated';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { CreditFacility } from '~/contexts/CreditFacilitiesProvider/useCreditFacilities';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { getDefaultDashboardDisplayStartDate } from '~/packages/common-frontend/constants';
import { useCheckIfAnyFacilitiesHaveBorrowingBaseQuery } from '~/packages/credit-facility/requests/requests.gql.generated';

/**
 * Given an array of all portcov results for multiple credit facilities
 * Returns the latest covenant evaluation results for each covenantKey if it exists in the latest credit agreement
 */
const getLatestCovenantResultsForCreditFacility = ({
  allCovenantResults,
  creditFacilityId,
  covenantKeys,
}: {
  allCovenantResults: (
    | SuccessFinancialCovenantGQLType
    | SuccessPortfolioCovenantGQLType
  )[];
  creditFacilityId: string;
  covenantKeys: Set<string>;
}) => {
  const latestVersionCovenantResultsForCreditFacility = allCovenantResults.reduce(
    (acc, covenantResult) => {
      const covenantKey = covenantResult.covenantKey;

      // filter first by creditFacilityId and covenants that exist in the latest credit agreement
      if (
        covenantResult.creditFacilityId === creditFacilityId &&
        covenantKeys.has(covenantKey)
      ) {
        // if the covenantKey is not yet in the acc,
        // or if the current result has a newer reportingDate,
        // or the same reportingDate but a higher version,
        // update the acc
        if (
          !acc[covenantKey] ||
          covenantResult.reportingDate > acc[covenantKey].reportingDate ||
          (covenantResult.reportingDate === acc[covenantKey].reportingDate &&
            covenantResult.version > acc[covenantKey].version)
        ) {
          // eslint-disable-next-line no-param-reassign
          acc[covenantKey] = covenantResult;
        }
      }
      return acc;
    },
    {} as Record<
      string,
      SuccessPortfolioCovenantGQLType | SuccessFinancialCovenantGQLType
    >,
  );

  return Object.values(latestVersionCovenantResultsForCreditFacility);
};

export const computeTotalsRow = (
  creditFacilityRows: FacilitiesTableRowData[],
  attributeValues: FacilitiesTableAttributeValueFragmentGQLType[],
): FacilitiesTableTotalRowData => {
  const addNumbers = (...args: number[]): number => {
    const total = args.reduce((acc, curr) => acc + curr, 0);
    return commonUtils.jsRound({ value: total, precision: 2 });
  };

  const stripedCreditFacilityRows = creditFacilityRows.map((row) =>
    _.omit(row, [
      'maturityDate',
      'closingDate',
      'dateOfLastFundingRequest',
      'receivableAdvanceRate',
      'unusedFees',
      'upfrontFees',
      'interestRates',
    ]),
  );

  const totalsRow = stripedCreditFacilityRows.reduce(
    (acc, row) => ({
      ...acc,
      globalCommitmentAmount: addNumbers(
        acc.globalCommitmentAmount,
        row.globalCommitmentAmount,
      ),
      globalLoanOutstanding: addNumbers(
        acc.globalLoanOutstanding,
        row.globalLoanOutstanding,
      ),
      globalUnusedCommitment: addNumbers(
        acc.globalUnusedCommitment,
        row.globalUnusedCommitment,
      ),
      capitalProviderCommitmentAmount: addNumbers(
        acc.capitalProviderCommitmentAmount,
        row.capitalProviderCommitmentAmount,
      ),
      capitalProviderLoanOutstanding: addNumbers(
        acc.capitalProviderLoanOutstanding,
        row.capitalProviderLoanOutstanding,
      ),
      capitalProviderUnused: addNumbers(
        acc.capitalProviderUnused,
        row.capitalProviderUnused,
      ),
      collectionsAccountBalance: addNumbers(
        acc.collectionsAccountBalance,
        row.collectionsAccountBalance,
      ),
      requiredReserve: addNumbers(acc.requiredReserve, row.requiredReserve),
      totalBorrowingBase: addNumbers(
        acc.totalBorrowingBase,
        row.totalBorrowingBase,
      ),
      borrowingBaseFromReceivables: addNumbers(
        acc.borrowingBaseFromReceivables,
        row.borrowingBaseFromReceivables,
      ),
      borrowingBaseCashContribution: addNumbers(
        acc.borrowingBaseCashContribution,
        row.borrowingBaseCashContribution,
      ),
      borrowingBaseAvailability: addNumbers(
        acc.borrowingBaseAvailability,
        row.borrowingBaseAvailability,
      ),
      accruedInterestAndFees: addNumbers(
        acc.accruedInterestAndFees,
        row.accruedInterestAndFees,
      ),
      financialCovenantsFailed: !_.isNil(row.financialCovenantsFailed)
        ? addNumbers(
            acc.financialCovenantsFailed ?? 0,
            row.financialCovenantsFailed,
          )
        : acc.financialCovenantsFailed,
      portfolioCovenantsFailed: !_.isNil(row.portfolioCovenantsFailed)
        ? addNumbers(
            acc.portfolioCovenantsFailed ?? 0,
            row.portfolioCovenantsFailed,
          )
        : acc.portfolioCovenantsFailed,
    }),
    {
      creditFacilityId: '',
      creditFacilityName: 'Portfolio totals',
      externalFacilityId: null,
      globalCommitmentAmount: 0,
      globalLoanOutstanding: 0,
      globalUnusedCommitment: 0,
      globalUtilization: 0,
      capitalProviderCommitmentAmount: 0,
      capitalProviderLoanOutstanding: 0,
      capitalProviderUnused: 0,
      capitalProviderUtilization: 0,
      collectionsAccountBalance: 0,
      requiredReserve: 0,
      totalBorrowingBase: 0,
      borrowingBaseFromReceivables: 0,
      borrowingBaseCashContribution: 0,
      borrowingBaseAvailability: 0,
      accruedInterestAndFees: 0,
      financialCovenantsFailed: null,
      portfolioCovenantsFailed: null,
    },
  );

  // Utilization based on totals
  totalsRow.globalUtilization =
    totalsRow.globalCommitmentAmount > 0
      ? totalsRow.globalLoanOutstanding / totalsRow.globalCommitmentAmount
      : 0;
  totalsRow.capitalProviderUtilization =
    totalsRow.capitalProviderCommitmentAmount > 0
      ? totalsRow.capitalProviderLoanOutstanding /
        totalsRow.capitalProviderCommitmentAmount
      : 0;

  const emptyAttributes = Array.from(
    new Set(attributeValues.map((av) => av.name)),
  ).reduce<{ [key: string]: null }>((acc, val) => {
    return { ...acc, [val]: null };
  }, {});

  return {
    ...totalsRow,
    ...emptyAttributes,
    maturityDate: null,
    closingDate: null,
    receivableAdvanceRate: null,
    dateOfLastFundingRequest: undefined,
    unusedFees: '-',
    upfrontFees: '-',
    interestRates: '-',
  };
};

const getAttributeValue = (
  av: FacilitiesTableAttributeValueFragmentGQLType,
) => {
  switch (av.__typename) {
    case 'BooleanAttributeValue':
      return av.booleanValue;
    case 'FloatAttributeValue':
      return av.floatValue;
    case 'IntegerAttributeValue':
      return av.integerValue;
    case 'PercentageRateAttributeValue':
      return av.percentageRateValue;
    case 'StringAttributeValue':
      return av.stringValue;
    case 'FullDateStrAttributeValue':
      return av.fullDateStrValue;
    case 'StringArrayAttributeValue':
      return av.stringArrayValue;
    default:
      return finleyAssertUnreachable(av);
  }
};
export type FacilitiesTableRowDataWithAttributes = FacilitiesTableRowData & {
  [key: string]: string | number | boolean | null;
};

const mapQueryDataToTableRows = ({
  organizationId,
  creditFacilities,
  facilitiesTableQueryData: {
    organization: { currentCreditAgreementSummaries },
    fundingRequests,
    expensesFeesLedgerForAllCountersigners,
    outstandingLedgerForAllCountersigners,
    outstandingLedgerForCapitalProvider,
    latestBorrowingBaseReports,
    getSuccessPortfolioCovenantsByCreditFacilityIds,
    getSuccessFinancialCovenantsByCreditFacilityIds,
    getAttributeValuesByParams,
  },
}: {
  organizationId: string;
  creditFacilities: CreditFacility[];
  facilitiesTableQueryData: GetFacilitiesTableDataQueryGQLType;
}): FacilitiesTableRowDataWithAttributes[] => {
  // Create Maps for O(1) lookups instead of O(n) find() per facility
  const summariesMap = new Map(
    currentCreditAgreementSummaries.map((s) => [s.creditFacilityId, s]),
  );
  const expensesFeesMap = new Map(
    expensesFeesLedgerForAllCountersigners.map((e) => [e.creditFacilityId, e]),
  );
  const outstandingAllMap = new Map(
    outstandingLedgerForAllCountersigners.map((o) => [o.creditFacilityId, o]),
  );
  const outstandingCpMap = new Map(
    outstandingLedgerForCapitalProvider.map((o) => [o.creditFacilityId, o]),
  );
  const borrowingBaseMap = new Map(
    latestBorrowingBaseReports
      .filter((r): r is NonNullable<typeof r> => r != null)
      .map((r) => [r.creditFacilityId, r]),
  );

  // Bucket data from all of the different queries by credit facility
  const creditFacilitiesWithData = creditFacilities.map((creditFacility) => {
    const creditAgreementSummary = summariesMap.get(creditFacility.id);
    if (!creditAgreementSummary) {
      throw Error('Missing "creditAgreementSummary" in facilities table');
    }

    const latestPortfolioCovenantResultsForCreditFacility = getLatestCovenantResultsForCreditFacility(
      {
        allCovenantResults: getSuccessPortfolioCovenantsByCreditFacilityIds,
        creditFacilityId: creditFacility.id,
        covenantKeys: new Set(creditAgreementSummary.portfolioCovenantKeys),
      },
    );

    const latestFinancialCovenantResultsForCreditFacility = getLatestCovenantResultsForCreditFacility(
      {
        allCovenantResults: getSuccessFinancialCovenantsByCreditFacilityIds,
        creditFacilityId: creditFacility.id,
        covenantKeys: new Set(creditAgreementSummary.financialCovenantKeys),
      },
    );

    return {
      creditFacility,
      creditAgreementSummary,
      lastFundingRequest: _.orderBy(
        fundingRequests.nodes.filter(
          (obj) => obj.creditFacilityId === creditFacility.id,
        ),
        'created_at',
        'desc',
      )[0] as
        | GetFacilitiesTableDataQueryGQLType['fundingRequests']['nodes'][number]
        | undefined,
      expensesFeesLedgerForFacilityForAllCountersigners: expensesFeesMap.get(
        creditFacility.id,
      ),
      outstandingLedgerForFacilityForAllCountersigners: outstandingAllMap.get(
        creditFacility.id,
      ),
      outstandingLedgerForFacilityForCapitalProvider: outstandingCpMap.get(
        creditFacility.id,
      ),
      portfolioCovenantsForFacility: latestPortfolioCovenantResultsForCreditFacility,
      financialCovenantsForFacility: latestFinancialCovenantResultsForCreditFacility,
      borrowingBaseReport: borrowingBaseMap.get(creditFacility.id),
    };
  });

  // Map credit facility data to table row derived values
  const emptyAttributes = Array.from(
    new Set(getAttributeValuesByParams.map((av) => av.name)),
  ).reduce<{ [key: string]: null }>((acc, val) => {
    return { ...acc, [val]: null };
  }, {});

  const cfAttributeMap = getAttributeValuesByParams.reduce<{
    [cfId: string]: {
      [name: string]: string | number | boolean | null | string[];
    };
  }>((acc, val) => {
    if (!acc[val.entityIdentifier]) {
      // eslint-disable-next-line no-param-reassign
      acc[val.entityIdentifier] = {};
    }
    // eslint-disable-next-line no-param-reassign
    acc[val.entityIdentifier][val.name] = getAttributeValue(val) ?? null;
    return acc;
  }, {});

  const creditFacilityRows = creditFacilitiesWithData.map(
    ({
      creditFacility,
      creditAgreementSummary,
      lastFundingRequest,
      expensesFeesLedgerForFacilityForAllCountersigners,
      outstandingLedgerForFacilityForAllCountersigners,
      outstandingLedgerForFacilityForCapitalProvider,
      portfolioCovenantsForFacility,
      financialCovenantsForFacility,
      borrowingBaseReport,
    }): FacilitiesTableRowData => {
      const { capitalProviders } = creditAgreementSummary;
      const isCapitalProvider = capitalProviders.some(
        (cp) => cp.entityId === organizationId,
      );
      const globalCommitmentAmount = _.sumBy(
        capitalProviders,
        (cp) => cp.commitmentAmount ?? 0,
      );
      const globalLoanOutstanding =
        outstandingLedgerForFacilityForAllCountersigners?.ledgerBalance ?? 0;

      const attributeMap = {
        ...emptyAttributes,
        ...(cfAttributeMap[creditFacility.id] ?? {}),
      };

      const borrowingBaseFromReceivables =
        borrowingBaseReport?.maxReceivableAdvanceAmount ?? 0;
      const borrowingBaseCashContribution =
        borrowingBaseReport?.totalCashBorrowingBaseAmount ?? 0;
      const totalBorrowingBase = commonUtils.jsRound({
        value: borrowingBaseFromReceivables + borrowingBaseCashContribution,
        precision: 2,
      });
      const maximumAvailableAmount = Math.min(
        totalBorrowingBase,
        borrowingBaseReport?.commitmentAmount ?? 0,
      );
      const borrowingBaseAvailability = commonUtils.jsRound({
        value:
          maximumAvailableAmount -
          (borrowingBaseReport?.currentPrincipalBalance ?? 0),
        precision: 2,
      });

      // Receivable advance rate is now pre-computed on the server
      // Normalize undefined to null for type compatibility
      const receivableAdvanceRate =
        creditAgreementSummary.receivableAdvanceRate ?? null;

      return {
        // "Facility"
        creditFacilityId: creditFacility.id,
        creditFacilityName: creditFacility.name,
        externalFacilityId: creditAgreementSummary.externalFacilityId ?? null,
        maturityDate: creditAgreementSummary.finalMaturityDate,
        closingDate: creditAgreementSummary.closingDate,
        revolvingPeriodEndDate:
          creditAgreementSummary.revolvingCommitmentTermination ?? undefined,

        // "Global Commitment Amount"
        globalCommitmentAmount: globalCommitmentAmount ?? 0,
        // "Global Outstanding Balance"
        globalLoanOutstanding,
        // "Global Unused Commitment"
        globalUnusedCommitment:
          globalCommitmentAmount -
          (outstandingLedgerForFacilityForAllCountersigners?.ledgerBalance ??
            0),
        // "Global Utilization"
        globalUtilization:
          globalCommitmentAmount > 0
            ? globalLoanOutstanding / globalCommitmentAmount
            : 0,

        // Capital provider-only cells. These are hidden for borrowers.
        ...(() => {
          const capitalProviderCommitmentAmount =
            capitalProviders.find((cp) => cp.entityId === organizationId)
              ?.commitmentAmount ?? 0;
          const capitalProviderLoanOutstanding =
            outstandingLedgerForFacilityForCapitalProvider?.ledgerBalance ?? 0;
          const capitalProviderUnused =
            capitalProviderCommitmentAmount - capitalProviderLoanOutstanding;

          return {
            // "Capital Provider Commitment Amount"
            capitalProviderCommitmentAmount,
            // "Capital Provider Outstanding Balance"
            capitalProviderLoanOutstanding,
            // "Capital Provider Unused Commitment"
            capitalProviderUnused,
            // "Capital Provider Utilization"
            capitalProviderUtilization:
              capitalProviderCommitmentAmount > 0
                ? capitalProviderLoanOutstanding /
                  capitalProviderCommitmentAmount
                : 0,
          };
        })(),

        // "Collections Account Balance"
        collectionsAccountBalance:
          borrowingBaseReport?.totalCollectionsAccountCash ?? 0,
        // "Required reserve"
        requiredReserve: borrowingBaseReport?.borrowingBaseReserveAmount ?? 0,
        // "Borrowing Base Availability"
        borrowingBaseAvailability,
        // "Total Borrowing Base"
        // !TODO @danitian Remove derivation during borrowing base migration phase 3
        totalBorrowingBase,
        // "Borrowing Base from Receivables"
        borrowingBaseFromReceivables,
        // "Receivable Advance Rate" (from CA configuration)
        receivableAdvanceRate,
        // "Borrowing Base from Cash"
        borrowingBaseCashContribution,
        // "Accrued Interest and Fees"
        accruedInterestAndFees:
          expensesFeesLedgerForFacilityForAllCountersigners?.ledgerBalance ?? 0,
        // "Interest Rates" - now pre-computed on server
        interestRates: capitalProviders
          .filter((cp) => !isCapitalProvider || cp.entityId === organizationId)
          .map((cp) => `${cp.entityName}: ${cp.interestRateDisplay}`)
          .join(', '),
        // "Unused Fees"
        unusedFees: capitalProviders.some((cp) => cp.unusedFee != null)
          ? capitalProviders
              .filter(
                (cp) => !isCapitalProvider || cp.entityId === organizationId,
              )
              .map((cp) => {
                if (cp.unusedFee != null) {
                  const formattedRate = commonUtils.numberToFormattedString(
                    cp.unusedFee,
                    'percentage',
                    false,
                  );
                  return `${cp.entityName}: ${formattedRate}`;
                }
                return `${cp.entityName}: n/a`;
              })
              .join(', ')
          : '-',
        // "Upfront Fees"
        upfrontFees: capitalProviders.some((cp) => cp.upFrontFee != null)
          ? capitalProviders
              .filter(
                (cp) => !isCapitalProvider || cp.entityId === organizationId,
              )
              .map((cp) => {
                if (cp.upFrontFee != null) {
                  const formattedRate = commonUtils.numberToFormattedString(
                    cp.upFrontFee,
                    'percentage',
                    false,
                  );
                  return `${cp.entityName}: ${formattedRate}`;
                }
                return `${cp.entityName}: n/a`;
              })
              .join(', ')
          : '-',
        // "Date of Last Funding Request"
        dateOfLastFundingRequest: lastFundingRequest
          ? commonDate.epochMsToFullDateStr(lastFundingRequest.created_at)
          : undefined,
        // "Portfolio Covenants"
        // null value will be rendered as '-'
        portfolioCovenantsFailed: portfolioCovenantsForFacility.length
          ? _.sumBy(
              portfolioCovenantsForFacility,
              (portfolioCovenantResult) => {
                if (portfolioCovenantResult.result !== 'PASS') {
                  return 1;
                }
                return 0;
              },
            )
          : null,
        // "Financial Covenants"
        // null value will be rendered as '-'
        financialCovenantsFailed: financialCovenantsForFacility.length
          ? _.sumBy(
              financialCovenantsForFacility,
              (financialCovenantResult) => {
                if (financialCovenantResult.result !== 'PASS') {
                  return 1;
                }
                return 0;
              },
            )
          : null,

        ...attributeMap,
      };
    },
  );
  return creditFacilityRows;
};

// Exports
export const useFacilitiesTableData = () => {
  const flags = useFeatureFlags();
  const { user } = useUserAuthOrThrow();
  const organizationId = user.organizationId;

  // Get credit facilities for this org
  const { facilities: creditFacilities } = useCreditFacilitiesOrThrow();
  const creditFacilityIds = creditFacilities.map(({ id }) => id);

  // Get the credit facility details that we can query in aggregate
  // TODO @sachinr4 - look into consolidating accounting calculations and aggregations used in this query (eg. outstanding balance)
  const facilitiesTableQuery = useGetFacilitiesTableDataQuery({
    // No credit facility IDs, most likely waiting on the credit facility query
    skip: creditFacilityIds.length === 0 || flags.isSlsAgentLender,
    fetchPolicy: 'cache-and-network',
    variables: {
      creditFacilityIds,
      creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
      organizationId,
      startDate: commonDate.fullDateStrToEpoch(
        getDefaultDashboardDisplayStartDate(),
      ),
      startDateStr: getDefaultDashboardDisplayStartDate(),
      getAttributeValuesParams: {
        organizationId,
        // one value per attribute + facility; without this every historical
        // value is returned and the row map below keeps the oldest
        latestOnly: true,
        entities: [
          {
            entityDomain: 'creditFacility',
            entityIdentifiers: creditFacilities.map((cf) => cf.id),
          },
        ],
      },
      useBatchOptimization: true,
    },
  });

  const checkHasBorrowingBaseQuery = useCheckIfAnyFacilitiesHaveBorrowingBaseQuery(
    {
      skip: creditFacilityIds.length === 0 || flags.isSlsAgentLender,
      variables: {
        creditFacilityIds,
        creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
        useBatchOptimization: true,
      },
    },
  );
  const hasBorrowingBase =
    checkHasBorrowingBaseQuery.data?.hasBorrowingBase ?? false;

  const { computedRows, totalsRow, av: attributeValues } = useMemo(() => {
    if (flags.isSlsAgentLender)
      return {
        computedRows: [],
        totalsRow: computeTotalsRow([], []),
        av: [],
      };
    const computed = facilitiesTableQuery.data
      ? mapQueryDataToTableRows({
          creditFacilities,
          facilitiesTableQueryData: facilitiesTableQuery.data,
          organizationId,
        })
      : [];

    const av = facilitiesTableQuery.data
      ? facilitiesTableQuery.data.getAttributeValuesByParams
      : [];

    return {
      av,
      computedRows: computed,
      totalsRow: computeTotalsRow(computed, av),
    };
  }, [
    creditFacilities,
    facilitiesTableQuery.data,
    flags.isSlsAgentLender,
    organizationId,
  ]);

  const balanceSheetExposureDataQuery = useGetBalanceSheetExposureDataQuery({
    skip: flags.isSlsAgentLender,
    variables: { organizationId },
    fetchPolicy: 'cache-and-network',
  });

  const balanceSheetImpactCalcs = balanceSheetExposureDataQuery.data ?? null;

  const error =
    facilitiesTableQuery.error ||
    balanceSheetExposureDataQuery.error ||
    checkHasBorrowingBaseQuery.error;
  const isHasBorrowingBaseLoading =
    checkHasBorrowingBaseQuery.loading || !checkHasBorrowingBaseQuery.data;
  const isBalanceSheetExposureLoading =
    balanceSheetExposureDataQuery.loading ||
    !balanceSheetExposureDataQuery.data;
  const isFacilitiesTableDataLoading =
    facilitiesTableQuery.loading || !facilitiesTableQuery.data;

  // memoized so consumers (the portfolio store) only see a new value when the
  // data actually changes; a fresh object every render re-renders the whole
  // facilities table on each provider render
  return useMemo(
    () => ({
      hasBorrowingBase,
      computedRows,
      totalsRow,
      balanceSheetImpactCalcs,
      attributeValues,
      attributeFiltersOptions: [],
      error,
      isAnyLoading:
        isFacilitiesTableDataLoading ||
        isBalanceSheetExposureLoading ||
        isHasBorrowingBaseLoading,
      isHasBorrowingBaseLoading,
      isBalanceSheetExposureLoading,
      isFacilitiesTableDataLoading,
    }),
    [
      hasBorrowingBase,
      computedRows,
      totalsRow,
      balanceSheetImpactCalcs,
      attributeValues,
      error,
      isHasBorrowingBaseLoading,
      isBalanceSheetExposureLoading,
      isFacilitiesTableDataLoading,
    ],
  );
};
